import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppStore } from "@/store/auth";
import { getCurrentUser, getSessionToken } from "@/lib/session";
import { feedApi, type FeedItem } from "@/lib/autoFeesApi";

interface Notification {
  id: string;
  type: "payment_received" | "payment_sent" | "trade_settled" | "announcement";
  title: string;
  message: string;
  currency: string;
  timestamp: string;
  read: boolean;
}

interface NotificationsResponse {
  count: number;
  unread: number;
  items: Notification[];
}

const TYPE_ICONS: Record<string, { icon: string; bg: string }> = {
  payment_received: { icon: "↓", bg: "bg-status-success-bg text-status-success" },
  payment_sent: { icon: "↑", bg: "bg-surface-tertiary text-dark-400" },
  trade_settled: { icon: "⇄", bg: "bg-status-info-bg text-status-info" },
  announcement: { icon: "📣", bg: "bg-brand-50 text-brand-700" },
};

/** Staff see their own notifications (Auto-Fees bookings, incoming
 * payments they opted into, applications); clients see their account's. */
export function NotificationBell({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const u = getCurrentUser();
  const staff = !!u && (String(u.role || "").startsWith("mfx_") || u.isSuperAdmin === true);
  return staff ? <StaffBell onNavigate={onNavigate} /> : <ClientBell onNavigate={onNavigate} />;
}

function bellTime(ts: string | null) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "Yesterday" : `${days}d ago`;
}

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="text-dark-400">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/** Where a staff notification's deeplink goes on the web ("" = nowhere). */
function staffPath(deeplink: string): string {
  if (deeplink.startsWith("/")) return deeplink;
  const [kind, id] = deeplink.split(/:(.*)/s);
  if (kind === "trade" && id) return `/settlements/record/Trade__c/${id}`;
  return "";
}

function StaffBell({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data, refetch } = useQuery({
    queryKey: ["notifications", "feed"], queryFn: feedApi.list, refetchInterval: 30_000, staleTime: 15_000,
  });
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);
  const items = data?.items ?? [];
  const unread = data?.unreadCount ?? 0;
  const click = async (n: FeedItem) => {
    if (!n.isRead) { try { await feedApi.read(n.id); } catch { /* still navigate */ } refetch(); }
    const path = staffPath(n.deeplink || "");
    if (path) { setOpen(false); onNavigate?.(path); }
  };
  const readAll = async () => { try { await feedApi.readAll(); } finally { refetch(); } };
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-surface-secondary dark:hover:bg-dark-500 transition-colors">
        <BellIcon />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-accent-500 text-white text-2xs font-bold rounded-full flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-96 bg-white dark:bg-dark-600 rounded-2xl shadow-modal border border-surface-border dark:border-dark-500 z-50 overflow-hidden animate-scale-in">
          <div className="px-4 py-3 border-b border-surface-border dark:border-dark-500 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-heading-sm text-dark-800 dark:text-white">Notifications</h3>
              <p className="text-caption text-dark-300">{unread ? `${unread} unread` : "All read"}</p>
            </div>
            {unread > 0 && <button onClick={readAll} className="text-caption font-medium text-accent-500 hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {!items.length ? (
              <div className="px-4 py-8 text-center text-body-sm text-dark-300">No notifications</div>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => click(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-secondary/50 dark:hover:bg-dark-500/50 transition-colors border-b border-surface-border/50 dark:border-dark-500/50 last:border-0 text-left ${n.isRead ? "" : "bg-brand-500/5"}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.isRead ? "bg-transparent" : "bg-accent-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-body-sm font-medium text-dark-700 dark:text-dark-100">{n.title}</div>
                  <div className="text-caption text-dark-400 dark:text-dark-200 [overflow-wrap:anywhere]">{n.body}</div>
                  <div className="text-2xs text-dark-200 mt-0.5">{bellTime(n.createdAt)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientBell({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const accountId = useAppStore((s) => s.accountId);

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ["notifications", accountId],
    queryFn: async () => {
      const base = import.meta.env.VITE_API_BASE_URL ?? "";
      const token = getSessionToken();
      const r = await fetch(`${base}/api/v1/notifications?accountId=${accountId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return r.json();
    },
    enabled: !!accountId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const items = (data?.items ?? []).slice(0, 10);
  const count = seen ? 0 : items.length;

  function handleOpen() { setOpen(!open); if (!open) setSeen(true); }

  function handleClickItem(n: Notification) {
    setOpen(false);
    if (!onNavigate) return;
    if (n.type === "announcement") onNavigate("/notifications");
    else if (n.type === "trade_settled") onNavigate(`/trades/${n.id}`);
    else onNavigate(`/transactions/${n.id}`);
  }

  function formatTime(ts: string) {
    if (!ts) return "";
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    return `${days}d ago`;
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={handleOpen} className="relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-surface-secondary dark:hover:bg-dark-500 transition-colors">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="text-dark-400">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-accent-500 text-white text-2xs font-bold rounded-full flex items-center justify-center">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white dark:bg-dark-600 rounded-2xl shadow-modal border border-surface-border dark:border-dark-500 z-50 overflow-hidden animate-scale-in">
          <div className="px-4 py-3 border-b border-surface-border dark:border-dark-500">
            <h3 className="text-heading-sm text-dark-800 dark:text-white">Notifications</h3>
            <p className="text-caption text-dark-300">Recent activity</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {!items.length ? (
              <div className="px-4 py-8 text-center text-body-sm text-dark-300">No recent notifications</div>
            ) : items.map((n) => {
              const style = TYPE_ICONS[n.type] ?? TYPE_ICONS.announcement;
              return (
                <button key={n.id} onClick={() => handleClickItem(n)} className="w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-secondary/50 dark:hover:bg-dark-500/50 transition-colors border-b border-surface-border/50 dark:border-dark-500/50 last:border-0 text-left">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${style.bg}`}>{style.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-body-sm font-medium text-dark-700 dark:text-dark-100">{n.title}</div>
                    <div className="text-caption text-dark-400 dark:text-dark-200 truncate">{n.message}</div>
                    <div className="text-2xs text-dark-200 mt-0.5">{formatTime(n.timestamp)}</div>
                  </div>
                </button>
              );
            })}
          </div>
          {items.length > 0 && (
            <button
              onClick={() => { setOpen(false); onNavigate?.("/notifications"); }}
              className="w-full px-4 py-2.5 text-body-sm font-medium text-accent-500 hover:bg-surface-secondary/50 dark:hover:bg-dark-500/50 border-t border-surface-border/50 dark:border-dark-500/50 text-center"
            >
              See all notifications
            </button>
          )}
        </div>
      )}
    </div>
  );
}
